/**
 * Route/page gating and role-based element visibility.
 */

import {
  canAccessPage,
  canPerformAction,
  isUserActive,
  hasAnyRole,
} from "/assets/js/permission_control/access-control.js";
import { PAGE_ACTION_POLICIES } from "/assets/js/permission_control/policy-map.js";

const SHELL_ACTION_STAMPS = [
  { selector: '[data-nav-group-wrap="financeiro"]', action: "financeiro.read" },
  { selector: '[data-page="catalogo"]', action: "catalog.read" },
  { selector: '[data-page="configuracoes"]', action: "configuracoes.read" },
  { selector: '[data-page="painel"]', action: "painel.read" },
  { selector: '[data-page="atendimento"]', action: "registros.read" },
  { selector: '[data-page="agenda"]', action: "atendimentos.read" },
  { selector: '[data-page="audio_fono"]', action: "exams.read" },
  { selector: '[data-page="pacientes"]', action: "patients.read" },
  { selector: '[data-page="manutencao"]', action: "ordens_servico.read" },
  { selector: '[data-page="parceiros"]', action: "parceiros.read" },
  { selector: '[data-page="relatorios"]', action: "relatorios.read" },
];

export function canUseProtectedSession() {
  return isUserActive();
}

/**
 * @param {string} pageName
 * @returns {boolean}
 */
export function guardPage(pageName) {
  const page = String(pageName || "")
    .trim()
    .toLowerCase();
  const actionKey = PAGE_ACTION_POLICIES[page];
  if (actionKey) return canPerformAction(actionKey);
  return canAccessPage(pageName);
}

/**
 * Runtime stamp of shell `data-require-action` after header inject.
 * MUST NOT edit `pages/components/header.html`.
 * @param {ParentNode} [root]
 */
export function stampShellActionAttributes(root = document) {
  if (!root || typeof root.querySelectorAll !== "function") return;
  for (const { selector, action } of SHELL_ACTION_STAMPS) {
    for (const node of root.querySelectorAll(selector)) {
      node.setAttribute("data-require-action", action);
    }
  }
}

/**
 * Hide nodes gated by data-require-roles and/or data-require-action.
 * Both attributes on the same node are AND-ed. Missing roles attr stays
 * backward compatible (roles-only nodes unchanged).
 * @param {ParentNode} [root]
 */
export function applyRoleVisibility(root = document) {
  const nodes = root.querySelectorAll("[data-require-roles], [data-require-action]");
  for (const node of nodes) {
    const rolesAttr = node.getAttribute("data-require-roles");
    const actionKey = String(node.getAttribute("data-require-action") || "").trim();

    let isAllowed = true;
    if (rolesAttr !== null) {
      const allowedRoles = String(rolesAttr)
        .split(",")
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
      isAllowed = hasAnyRole(allowedRoles);
    }
    if (isAllowed && actionKey) {
      isAllowed = canPerformAction(actionKey);
    }

    node.classList.toggle("hidden", !isAllowed);
    if (!isAllowed) {
      node.setAttribute("aria-hidden", "true");
      node.setAttribute("tabindex", "-1");
    } else {
      node.removeAttribute("aria-hidden");
      node.removeAttribute("tabindex");
    }
  }
  collapseEmptyNavGroups(root);
}

/**
 * Hide a nav group when every child `data-page` link is hidden.
 * @param {ParentNode} [root]
 */
export function collapseEmptyNavGroups(root = document) {
  if (!root || typeof root.querySelectorAll !== "function") return;
  for (const wrap of root.querySelectorAll("[data-nav-group-wrap]")) {
    if (wrap.classList.contains("hidden") && wrap.hasAttribute("data-require-action")) {
      continue;
    }
    const links = wrap.querySelectorAll("a[data-page]");
    if (!links.length) continue;
    const anyVisible = [...links].some((link) => !link.classList.contains("hidden"));
    if (anyVisible) continue;
    wrap.classList.add("hidden");
    wrap.setAttribute("aria-hidden", "true");
  }
}

export const PermissionGuards = {
  canUseProtectedSession,
  guardPage,
  stampShellActionAttributes,
  applyRoleVisibility,
  collapseEmptyNavGroups,
};
